import React from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@digital-twins/design-system/components/ui/tooltip'

import {
  HeatGridColumn,
  HeatGridColumnGroup,
  HeatGridRow,
  HeatGridRowGroup,
  PlotStatView,
} from '@digital-twins/slide-schemas/generated/plot-types'

import { LightboxImage } from '../../components/primitives/LightboxImage/LightboxImage'
import { TextClamp } from '../../components/text/TextClamp'
import { ChartLabel } from '../_shared/ChartLabel'
import { formatHeatAverage, formatHeatValue } from './format'
import { HeatGridPlotData, cellInView, heatGridDrawnView, heatGridStatFormat } from './HeatGridPlot'
import { heatFill, heatText } from './style'

/**
 * `heat-grid` at the deck matrix-rating slide's geometry (plots plan Q).
 *
 * The same data every heat surface draws (`HeatGridPlot`), laid out in the
 * slide's measured panel: the row-label and average tracks, the cell heights
 * budgeted against the panel, the row group headers, the thumbnails. The
 * slide keeps its chrome (title band, page cue, swatches, scale legend);
 * this draws the column headers and the row stack.
 *
 * Every figure, heat and mark is Python's (`canonical_analysis/heat_grid_plot.py`).
 * Versions print under their measure (`columnGroups`), so a header never
 * composes "measure · version".
 */

/**
 * Every band of the heatmap column except the row stack, as the pixels it occupies
 * (the panel is a fixed box with no scroll, so the row stack is what these leave).
 */
export const MATRIX_RATING_CHROME = {
  /** Panel padding: `40px 32px 20px`. The foot pad keeps the legend off the edge. */
  padTop: 40,
  padBottom: 20,
  /** 3 clamped title lines at 14px/1.2, rounded up. */
  titleBand: 51,
  /** Title band to column headers. */
  titleGap: 20,
  /** 2 clamped header lines at 10px/1.15. */
  columnHeaders: 23,
  /** Column headers to the first row. */
  headerGap: 8,
  /** Between rows in the stack. */
  rowGap: 2,
  /** 12px margin + 2 clamped legend lines at 11px/1.4. */
  legend: 43,
} as const

/**
 * Measure header lines by measure count (owner decision 21). From 7 measures the
 * columns are narrow enough that a short attribute ("Grabs my attention") would
 * be cut at 2 lines, so the header band takes a third 10px line rather than the
 * label being shortened. The only geometry this changes is the header band and
 * the row stack under it, which gives that line back.
 */
export const MATRIX_THREE_LINE_HEADER_MIN_MEASURES = 7

/** How many lines a measure header may take for this many measures. */
export function matrixColumnHeaderLines(measureCount: number) {
  if (measureCount >= MATRIX_THREE_LINE_HEADER_MIN_MEASURES) {
    return 3
  }
  return 2
}

/** The header band's height: its lines at 10px/1.15, rounded up (23px at 2 lines, 35px at 3). */
export function matrixColumnHeadersPx(measureCount: number) {
  return Math.ceil(matrixColumnHeaderLines(measureCount) * 10 * 1.15)
}

/** The measure line over version columns (`columnGroups`): 2 lines at 10px/1.15 plus its rule. */
export const MATRIX_COLUMN_GROUP_HEADER_PX = 28

/** The gap between the measure line and the version headers under it. */
export const MATRIX_COLUMN_GROUP_GAP_PX = 2

/**
 * The measure line's height for this many measures: over versions the measure
 * header is the group line, so it takes the same lines as a flat measure header
 * (a third from seven measures), plus its rule.
 */
export function matrixColumnGroupHeaderPx(measureCount: number) {
  return matrixColumnHeadersPx(measureCount) + (MATRIX_COLUMN_GROUP_HEADER_PX - MATRIX_RATING_CHROME.columnHeaders)
}

/**
 * Pixels the row stack (cells plus its 2px gaps) may occupy, given the height of the
 * box the heatmap is drawn in and whether it draws its own title.
 */
export function matrixRowsBudget(params: { columnHeight: number; hasTitle: boolean }) {
  const { columnHeight, hasTitle } = params
  const chrome =
    (hasTitle ? MATRIX_RATING_CHROME.titleBand + MATRIX_RATING_CHROME.titleGap : 0) +
    MATRIX_RATING_CHROME.columnHeaders +
    MATRIX_RATING_CHROME.headerGap +
    MATRIX_RATING_CHROME.legend
  return Math.max(0, columnHeight - chrome)
}

/**
 * The design's cell height per row-count bucket. These are ceilings, not answers:
 * the chart breathes at two rows and tightens as rows are added.
 */
function matrixCellHeightCeiling(rowCount: number) {
  if (rowCount <= 4) {
    return 128
  }
  if (rowCount <= 6) {
    return 84
  }
  if (rowCount <= 8) {
    return 62
  }
  return 40
}

/** Cell height for a row count, floored against the budget the caller actually has. */
export function matrixCellHeight(rowCount: number, rowsBudgetPx: number) {
  const rows = Math.max(1, rowCount)
  const gaps = (rows - 1) * MATRIX_RATING_CHROME.rowGap
  const affordable = Math.floor((rowsBudgetPx - gaps) / rows)
  // 24px is the smallest box that holds the 18px value it prints.
  return Math.max(24, Math.min(matrixCellHeightCeiling(rows), affordable))
}

const ROW_LABEL_FONT_PX = 12
const ROW_LABEL_LINE_HEIGHT = 1.15
const ROW_LABEL_LINE_PX = ROW_LABEL_FONT_PX * ROW_LABEL_LINE_HEIGHT
const ROW_LABEL_MAX_LINES = 4
const ROW_THUMBNAIL_BLOCK_PX = 52
const ROW_GROUP_HEADER_PX = 30
const ROW_GROUP_THUMBNAIL_PX = 26
const FIGMA_GRAY = 'var(--slide-text-muted)'
const AVERAGE_BORDER = '#9B9B9B'
const EMPTY_FILL = 'var(--slide-neutral-light)'

/** The rows that open a group, by index: a row whose group differs from the row above. */
export function matrixRowGroupStarts(
  rows: Array<{ groupId?: string | null }>,
  rowGroups: Array<HeatGridRowGroup> | null | undefined,
): Map<number, HeatGridRowGroup> {
  const byId = new Map((rowGroups || []).map((group) => [group.id, group]))
  const starts = new Map<number, HeatGridRowGroup>()
  rows.forEach((row, index) => {
    const group = row.groupId ? byId.get(row.groupId) : undefined
    if (group && rows[index - 1]?.groupId !== row.groupId) {
      starts.set(index, group)
    }
  })
  return starts
}

/** The row stack's budget once the group headers it draws are paid for. */
export function matrixRowsBudgetAfterGroups(rowsBudgetPx: number, groupHeaderCount: number) {
  return Math.max(0, rowsBudgetPx - groupHeaderCount * (ROW_GROUP_HEADER_PX + MATRIX_RATING_CHROME.rowGap))
}

/** How many label lines the row can show without growing past its cell (at most four). */
export function matrixRowLabelMaxLines(cellHeight: number, hasThumbnail: boolean) {
  const reserved = hasThumbnail ? ROW_THUMBNAIL_BLOCK_PX : 0
  const fitted = Math.floor((cellHeight - reserved) / ROW_LABEL_LINE_PX)
  return Math.max(1, Math.min(ROW_LABEL_MAX_LINES, fitted))
}

function groupSpans(columns: ReadonlyArray<HeatGridColumn>, groups: ReadonlyArray<HeatGridColumnGroup>) {
  const byId = new Map(groups.map((group) => [group.id, group]))
  const spans: Array<{ group: HeatGridColumnGroup | null; span: number; key: string }> = []
  columns.forEach((column) => {
    const group = column.groupId ? byId.get(column.groupId) ?? null : null
    const last = spans[spans.length - 1]
    if (last && group && last.group?.id === group.id) {
      last.span += 1
    } else {
      spans.push({ group, span: 1, key: column.id })
    }
  })
  return spans
}

function MatrixCell(props: {
  row: HeatGridRow
  column: HeatGridColumn
  cellHeight: number
  /** The stat view the grid draws (`data.defaultView`); a measure cell prints its figure in it. */
  view: PlotStatView
  statDecimals?: number
  locale?: string
  renderMode?: 'interactive' | 'static'
}) {
  const { row, column, cellHeight, view, statDecimals, locale, renderMode } = props
  const cell = row.cells.find((entry) => entry.columnId === column.id)
  const cellView = column.kind === 'measure' ? view : 'mean'
  const shown = cellInView(cell, cellView)
  const statFormat = heatGridStatFormat(cellView, { statDecimals })
  const heat = column.kind === 'change' ? null : shown.heat
  const text = formatHeatValue(shown.value, cellView === 'mean' ? column : statFormat, locale)
  // Top N leads; Bottom N prints smaller beneath (decision 20).
  const secondaryText = formatHeatValue(shown.secondary, statFormat, locale)
  const isStrongest = shown.isStrongest
  const hasHeat = typeof heat === 'number' && Number.isFinite(heat)
  const textColor = hasHeat ? heatText(heat) : 'var(--slide-text-primary)'
  const bottomLabel = secondaryText && column.boxLabels ? `${column.boxLabels.bottom}: ${secondaryText}` : null
  const cellTitle = text === null ? column.label : [`${column.label}: ${text}`, bottomLabel].filter(Boolean).join(', ')
  const isStatic = renderMode === 'static'
  const body = (
    <div
      data-testid="matrix-rating-cell"
      data-row-id={row.id}
      data-column-id={column.id}
      data-strongest={isStrongest ? 'true' : 'false'}
      data-change={column.kind === 'change' ? 'true' : 'false'}
      data-empty={text === null ? 'true' : 'false'}
      data-view={cellView}
      title={isStatic ? cellTitle : undefined}
      style={{
        height: cellHeight,
        borderRadius: 6,
        background: hasHeat ? heatFill(heat) : EMPTY_FILL,
        border: isStrongest ? `2px solid ${textColor}` : '1px solid transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <TextClamp
        minLines={1}
        maxLines={1}
        lineHeight="1.2"
        // The cell is a column (figure, then Bottom N): the clamp keeps the cell's width.
        style={{ fontSize: 18, fontWeight: 600, color: textColor, textAlign: 'center', maxWidth: '100%' }}
      >
        {text ?? ''}
      </TextClamp>
      {secondaryText ? (
        <span data-testid="matrix-rating-cell-secondary" style={{ fontSize: 11, fontWeight: 500, lineHeight: 1.2, color: textColor, opacity: 0.85 }}>
          {secondaryText}
        </span>
      ) : null}
    </div>
  )
  if (isStatic) {
    return body
  }
  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>{body}</TooltipTrigger>
        <TooltipContent data-testid="matrix-rating-cell-tooltip">{cellTitle}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function MatrixAverage(props: {
  value: number | null | undefined
  columns: ReadonlyArray<HeatGridColumn>
  cellHeight: number
  locale?: string
  title: string
  renderMode?: 'interactive' | 'static'
}) {
  const { value, columns, cellHeight, locale, title, renderMode } = props
  const text = formatHeatAverage(value, columns, locale) ?? ''
  const tooltip = text ? `${title}: ${text}` : title
  const isStatic = renderMode === 'static'
  const body = (
    <div
      data-testid="matrix-rating-row-average"
      title={isStatic ? tooltip : undefined}
      style={{
        height: cellHeight,
        borderRadius: 6,
        background: 'transparent',
        border: `1px solid ${AVERAGE_BORDER}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <TextClamp
        minLines={1}
        maxLines={1}
        lineHeight="1.2"
        style={{ fontSize: 14, fontWeight: 600, color: 'var(--slide-text-primary)', textAlign: 'center' }}
      >
        {text}
      </TextClamp>
    </div>
  )
  if (isStatic) {
    return body
  }
  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>{body}</TooltipTrigger>
        <TooltipContent data-testid="matrix-rating-row-average-tooltip">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export interface HeatGridMatrixProps {
  data: HeatGridPlotData
  /** Pixels the header rows and the row stack may occupy together. */
  rowsBudgetPx: number
  /** Top margin above the column headers (0 inside a breakdown). */
  headerMarginTop: number
  locale?: string
  renderMode?: 'interactive' | 'static'
  /** The row label, when the host edits it (the deck passes its editable text). */
  renderRowLabel?: (
    row: HeatGridRow,
    index: number,
    maxLines: number,
    chartLabel: { hintIndex: number },
  ) => React.ReactNode
}

/**
 * Plots plan LB: on an interactive surface a label and its (i) share a row
 * (`ChartLabel` renders the (i) as the clamp's next sibling). A static render
 * keeps the label exactly where it was, alone.
 */
function labelRow(
  label: React.ReactNode,
  renderMode: 'interactive' | 'static' | undefined,
  style: React.CSSProperties,
) {
  if (renderMode === 'static') {
    return label
  }
  return <div style={{ display: 'flex', gap: 3, minWidth: 0, ...style }}>{label}</div>
}

export function HeatGridMatrix(props: HeatGridMatrixProps) {
  const { data, rowsBudgetPx, headerMarginTop, locale, renderMode, renderRowLabel } = props
  const columns = data.columns
  const groups = data.columnGroups ?? []
  const columnCount = Math.max(1, columns.length)
  // A deck built with stat views draws the one Python stamped as its default (decision 20,
  // AG-2); a grid built without views is Average.
  const view = heatGridDrawnView(data, data.defaultView)
  // The row averages are Average figures: drawn in the Average view only.
  const showAverageColumn =
    view === 'mean' && columns.length > 1 && data.rows.some((row) => typeof row.average === 'number')
  const rowLabelWidth = columnCount >= 7 ? 136 : 176
  const averageColumnWidth = columnCount >= 7 ? 76 : 92
  const groupStarts = matrixRowGroupStarts(data.rows, data.rowGroups)
  const groupHeaderPx = matrixColumnGroupHeaderPx(groups.length)
  const groupHeaderBudget = groups.length > 0 ? groupHeaderPx + MATRIX_COLUMN_GROUP_GAP_PX : 0
  // With groups the header row names versions (2 lines); the measure is the group line above.
  const headerLines = groups.length > 0 ? 2 : matrixColumnHeaderLines(columns.length)
  const headerBandPx = groups.length > 0 ? MATRIX_RATING_CHROME.columnHeaders : matrixColumnHeadersPx(columns.length)
  // A static render draws every header whole (wave D: complete text ships, cut text
  // fails), so a long split label ("Chose ... (n = 24)") wraps past the declared band.
  // The bands it actually drew are measured, and what they take past their declared
  // height comes out of the row stack, so the grid still fits its panel.
  const headerRef = React.useRef<HTMLDivElement | null>(null)
  const groupHeaderRef = React.useRef<HTMLDivElement | null>(null)
  const [drawnHeaderPx, setDrawnHeaderPx] = React.useState(0)
  const [drawnGroupHeaderPx, setDrawnGroupHeaderPx] = React.useState(0)
  React.useLayoutEffect(() => {
    if (renderMode !== 'static') return
    const header = headerRef.current?.getBoundingClientRect().height ?? 0
    const group = groupHeaderRef.current?.getBoundingClientRect().height ?? 0
    setDrawnHeaderPx((prev) => (prev === header ? prev : header))
    setDrawnGroupHeaderPx((prev) => (prev === group ? prev : group))
  })
  // `rowsBudgetPx` pays for a 2-line header band; a third line comes out of the row stack.
  const extraHeaderPx = Math.max(headerBandPx, drawnHeaderPx) - MATRIX_RATING_CHROME.columnHeaders
  const extraGroupHeaderPx = groups.length > 0 ? Math.max(0, drawnGroupHeaderPx - groupHeaderPx) : 0
  const cellHeight = matrixCellHeight(
    data.rows.length,
    matrixRowsBudgetAfterGroups(
      rowsBudgetPx - groupHeaderBudget - extraGroupHeaderPx - extraHeaderPx,
      groupStarts.size,
    ),
  )
  const gridTemplateColumns = showAverageColumn
    ? `${rowLabelWidth}px repeat(${columnCount}, minmax(0, 1fr)) ${averageColumnWidth}px`
    : `${rowLabelWidth}px repeat(${columnCount}, minmax(0, 1fr))`
  const avgLabel = data.labels.avg_column ?? ''

  return (
    <>
      {groups.length > 0 ? (
        <div
          ref={groupHeaderRef}
          data-testid="matrix-rating-column-groups"
          style={{
            display: 'grid',
            gridTemplateColumns,
            columnGap: 2,
            alignItems: 'end',
            marginTop: headerMarginTop,
            minHeight: groupHeaderPx,
            flexShrink: 0,
          }}
        >
          <div />
          {groupSpans(columns, groups).map((entry, entryIndex) => (
            <div
              key={entry.key}
              data-testid={entry.group ? 'matrix-rating-column-group' : undefined}
              data-group-id={entry.group?.id}
              style={{
                gridColumn: `span ${entry.span}`,
                borderBottom: entry.group ? `1px solid ${AVERAGE_BORDER}` : undefined,
                paddingBottom: 2,
                minWidth: 0,
              }}
            >
              {entry.group && renderMode === 'static' ? (
                <TextClamp
                  tooltipOnTruncate
                  renderMode={renderMode}
                  minLines={1}
                  maxLines={matrixColumnHeaderLines(groups.length)}
                  lineHeight="1.15"
                  style={{ fontSize: 10, fontWeight: 600, color: 'var(--slide-text-primary)', textAlign: 'center' }}
                >
                  {entry.group.label}
                </TextClamp>
              ) : entry.group ? (
                labelRow(
                  <ChartLabel
                    text={entry.group.label}
                    hintIndex={data.rows.length + columns.length + entryIndex}
                    hintSize={10}
                    renderMode={renderMode}
                    maxLines={matrixColumnHeaderLines(groups.length)}
                    lineHeight="1.15"
                    style={{ fontSize: 10, fontWeight: 600, color: 'var(--slide-text-primary)', textAlign: 'center', minWidth: 0 }}
                  />,
                  renderMode,
                  { alignItems: 'center', justifyContent: 'center' },
                )
              ) : null}
            </div>
          ))}
          {showAverageColumn ? <div /> : null}
        </div>
      ) : null}
      <div
        ref={headerRef}
        style={{
          display: 'grid',
          gridTemplateColumns,
          columnGap: 2,
          alignItems: 'end',
          marginTop: groups.length > 0 ? MATRIX_COLUMN_GROUP_GAP_PX : headerMarginTop,
          minHeight: headerBandPx,
          flexShrink: 0,
        }}
      >
        <div />
        {columns.map((column, columnIndex) =>
          renderMode === 'static' ? (
            <TextClamp
              key={column.id}
              data-testid="matrix-rating-column-header"
              data-column-id={column.id}
              tooltipOnTruncate
              renderMode={renderMode}
              minLines={1}
              maxLines={headerLines}
              lineHeight="1.15"
              style={{ fontSize: 10, fontWeight: 600, color: FIGMA_GRAY, textAlign: 'center' }}
            >
              {column.label}
            </TextClamp>
          ) : (
            <div
              key={column.id}
              data-column-id={column.id}
              style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 3, minWidth: 0 }}
            >
              <ChartLabel
                data-testid="matrix-rating-column-header"
                text={column.label}
                hintIndex={data.rows.length + columnIndex}
                hintSize={10}
                renderMode={renderMode}
                maxLines={headerLines}
                lineHeight="1.15"
                style={{ fontSize: 10, fontWeight: 600, color: FIGMA_GRAY, textAlign: 'center', minWidth: 0 }}
              />
            </div>
          ),
        )}
        {showAverageColumn ? (
          <TextClamp
            data-testid="matrix-rating-column-header"
            minLines={1}
            maxLines={1}
            lineHeight="1.15"
            style={{ fontSize: 10, fontWeight: 700, color: 'var(--slide-text-primary)', textAlign: 'center' }}
          >
            {avgLabel}
          </TextClamp>
        ) : null}
      </div>

      <div
        data-testid="matrix-rating-row-stack"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: MATRIX_RATING_CHROME.rowGap,
          marginTop: MATRIX_RATING_CHROME.headerGap,
          flexShrink: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {data.rows.map((row, rowIndex) => {
          const groupStart = groupStarts.get(rowIndex)
          const maxLines = matrixRowLabelMaxLines(cellHeight, Boolean(row.imageUrl))
          return (
            <React.Fragment key={row.id}>
              {groupStart ? (
                <div
                  data-testid={`matrix-rating-row-group-${groupStart.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    height: ROW_GROUP_HEADER_PX,
                    minWidth: 0,
                    flexShrink: 0,
                  }}
                >
                  {groupStart.imageUrl ? (
                    <LightboxImage
                      src={groupStart.imageUrl}
                      alt={groupStart.label}
                      data-testid="matrix-rating-row-group-thumbnail"
                      style={{ height: ROW_GROUP_THUMBNAIL_PX, width: 'auto', borderRadius: 4, objectFit: 'contain', flexShrink: 0 }}
                    />
                  ) : null}
                  <TextClamp
                    data-testid="matrix-rating-row-group-label"
                    tooltipOnTruncate
                    renderMode={renderMode}
                    minLines={1}
                    maxLines={1}
                    lineHeight="1.2"
                    style={{ fontSize: 12, fontWeight: 600, color: 'var(--slide-text-primary)', minWidth: 0 }}
                  >
                    {groupStart.label}
                  </TextClamp>
                </div>
              ) : null}
              <div style={{ display: 'grid', gridTemplateColumns, columnGap: 2, alignItems: 'center' }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 4,
                    width: '100%',
                    paddingRight: 18,
                    boxSizing: 'border-box',
                  }}
                >
                  {row.imageUrl ? (
                    <LightboxImage
                      src={row.imageUrl}
                      alt={row.label}
                      data-testid="matrix-rating-row-thumbnail"
                      style={{ height: 48, width: 'auto', borderRadius: 4, objectFit: 'contain', flexShrink: 0 }}
                    />
                  ) : null}
                  {labelRow(
                    renderRowLabel ? (
                      renderRowLabel(row, rowIndex, maxLines, { hintIndex: rowIndex })
                    ) : (
                      <ChartLabel
                        data-testid="matrix-rating-row-label"
                        text={row.label}
                        hintIndex={rowIndex}
                        hintSize={10}
                        renderMode={renderMode}
                        maxLines={maxLines}
                        lineHeight={`${ROW_LABEL_LINE_HEIGHT}`}
                        style={{ fontSize: ROW_LABEL_FONT_PX, fontWeight: 500, color: 'var(--slide-text-primary)', minWidth: 0 }}
                      />
                    ),
                    renderMode,
                    { alignItems: 'center', width: '100%' },
                  )}
                </div>
                {columns.map((column) => (
                  <MatrixCell
                    key={`${row.id}-${column.id}`}
                    row={row}
                    column={column}
                    cellHeight={cellHeight}
                    view={view}
                    statDecimals={data.statDecimals}
                    locale={locale}
                    renderMode={renderMode}
                  />
                ))}
                {showAverageColumn ? (
                  <MatrixAverage
                    value={row.average}
                    columns={columns}
                    cellHeight={cellHeight}
                    locale={locale}
                    title={`${row.label} (${avgLabel})`}
                    renderMode={renderMode}
                  />
                ) : null}
              </div>
            </React.Fragment>
          )
        })}
      </div>
    </>
  )
}

export const MATRIX_ROW_LABEL_TYPE = {
  fontSize: ROW_LABEL_FONT_PX,
  lineHeight: ROW_LABEL_LINE_HEIGHT,
} as const
